import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Check,
  FileText,
  GraduationCap,
  Loader2,
  Palette,
  Printer,
  Quote,
  Sparkles,
} from 'lucide-react';
import { ELEMENTOS_DOCUMENTO, formatarPreco, planoPorId } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api, ErroApi } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';

/**
 * A área de **quem escreve** — o outro produto, com porta própria.
 *
 * É a tela pública de entrada do editor de documentos (trabalho acadêmico, artigo,
 * livro): quem chega aqui não veio procurar agenda. Duas portas, e é isso que a
 * tela precisa deixar claro de relance:
 *
 * 1. **conhecer de graça** — a pessoa cria a conta e o acesso sai na hora (o plano
 *    gratuito do catálogo, `documentos-conhecer`); quem já está logado pega com um
 *    clique, sem passar por checkout;
 * 2. **assinar** — o plano *Documentos*, que é só o editor (sem os miolos de agenda),
 *    com o preço que o administrador edita no painel.
 *
 * **Mobile primeiro**: tudo em uma coluna, as duas portas empilhadas (lado a lado só
 * a partir de `lg`), botões de largura cheia no celular e alvos de toque de 40 px —
 * o polegar decide, e a tela foi montada pensando nele antes do mouse. Os números e
 * a lista do que o editor faz saem do catálogo do produto
 * (`ELEMENTOS_DOCUMENTO`), não de texto solto.
 */
export function Escrever() {
  const { usuario, recarregar } = useAuth();
  const navegar = useNavigate();
  const [pedindo, setPedindo] = useState(false);
  const [erro, setErro] = useState('');
  const [recado, setRecado] = useState('');

  const jaTem = Boolean(usuario?.editorDeDocumentos);

  const planoPago = planoPorId('documentos');
  const planoGratis = planoPorId('documentos-conhecer');

  /** Quantas peças do documento o gerador monta sozinho (do catálogo do produto). */
  const pecas = ELEMENTOS_DOCUMENTO.length;

  const conhecer = async () => {
    if (!usuario) {
      navegar('/criar-conta?acesso=documentos');
      return;
    }
    setPedindo(true);
    setErro('');
    try {
      const resposta = await api.pedirAcessoAoEditor();
      setRecado(
        resposta.jaTinha
          ? 'Seu acesso já estava liberado. Bom trabalho!'
          : 'Acesso liberado! Você já pode escrever.',
      );
      await recarregar();
    } catch (causa) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível liberar o acesso agora.');
    } finally {
      setPedindo(false);
    }
  };

  const O_QUE_FAZ = [
    {
      icone: GraduationCap,
      titulo: 'Trabalho acadêmico na ABNT',
      texto: 'NBR 14724: margens, entrelinha, citações (10520) e referências (6023) já formatadas.',
    },
    {
      icone: FileText,
      titulo: 'Artigo científico',
      texto: 'Título, resumo com palavras-chave, abstract e seções numeradas.',
    },
    {
      icone: BookOpen,
      titulo: 'Livro em A4 ou A5',
      texto: 'Capítulos numerados, cabeçalho, filete, capitular e número de página alternado.',
    },
    {
      icone: Quote,
      titulo: 'Citação em um clique',
      texto: 'O botão Citar escreve a citação autor-data na norma e a referência entra na lista.',
    },
    {
      icone: Palette,
      titulo: 'A cara do documento',
      texto: 'Faixa de cabeçalho e rodapé, ornamento, folha de anotação e imagem de capa.',
    },
    {
      icone: Printer,
      titulo: 'PDF do navegador',
      texto: 'Sem instalar nada: a folha sai no tamanho certo, com quebra e numeração no lugar.',
    },
  ];

  return (
    <div className="space-y-10 py-4 sm:space-y-14">
      {/* O herói: o produto dito em uma linha, com as duas portas logo abaixo. */}
      <section
        className="secao-forte mx-auto max-w-3xl space-y-5 rounded-2xl px-5 py-8 text-center shadow-sm sm:px-8 sm:py-10"
        style={{ '--tom': 'var(--cor-alegre-1)' } as React.CSSProperties}
      >
        <Badge tom="principal" className="mx-auto">
          <Sparkles className="size-3" />
          Sem instalar nada · o PDF sai do navegador
        </Badge>
        <h1 className="text-3xl font-semibold leading-[1.15] tracking-tight sm:text-4xl">
          Escreva o trabalho, o artigo ou o livro — a formatação é do gerador
        </h1>
        <p className="mx-auto max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          Você escreve no editor e vê o papel ao lado, folha por folha. Margens, citações, referências,
          sumário, capítulos numerados, cabeçalho e número de página saem prontos — e {pecas} peças do
          documento (capa, folha de rosto, resumo, sumário, referências...) são montadas por você marcar.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          {jaTem ? (
            <Button asChild tamanho="grande" className="w-full sm:w-auto">
              <Link to="/documentos">
                Abrir meus documentos
                <ArrowRight />
              </Link>
            </Button>
          ) : (
            <Button
              tamanho="grande"
              className="whitespace-normal! h-auto! py-2.5 w-full sm:w-auto"
              disabled={pedindo}
              onClick={() => void conhecer()}
            >
              {pedindo ? <Loader2 className="animate-spin" /> : <Sparkles />}
              {usuario ? 'Quero conhecer de graça' : 'Criar conta e conhecer de graça'}
            </Button>
          )}
          <Button asChild variant="contorno" tamanho="grande" className="whitespace-normal! h-auto! py-2.5 w-full sm:w-auto">
            <Link to="/documentos">Já tenho acesso</Link>
          </Button>
        </div>

        <p className="text-xs leading-relaxed text-muted-foreground">
          {planoGratis
            ? `O acesso de conhecimento é gratuito e sai na hora${usuario ? '' : ', assim que a conta existir'}: ${planoGratis.resumo}`
            : 'O acesso de conhecimento é gratuito e sai na hora.'}
        </p>

        {recado ? (
          <p className="rounded-md border border-border bg-background/70 p-3 text-sm" data-acesso-liberado="">
            <Check className="mr-1 inline size-4" />
            {recado}
          </p>
        ) : null}
        {erro ? (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm" data-erro-do-acesso="">
            {erro}
          </p>
        ) : null}
      </section>

      {/* O que esta área faz — em uma coluna no celular, três no computador. */}
      <section className="space-y-4">
        <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">
          O que o editor faz por você
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {O_QUE_FAZ.map(({ icone: Icone, titulo, texto }, indice) => (
            <Card key={titulo} className="cartao-tom faixa-tom" style={{ '--tom': TONS[indice % TONS.length] } as React.CSSProperties}>
              <CardHeader>
                <span
                  className="flex size-9 items-center justify-center rounded-md"
                  style={{
                    background: `color-mix(in oklab, ${TONS[indice % TONS.length]} 14%, transparent)`,
                    color: TONS[indice % TONS.length],
                  }}
                >
                  <Icone className="size-4" />
                </span>
                <CardTitle className="pt-1">{titulo}</CardTitle>
                <CardDescription>{texto}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </section>

      {/* As duas portas, com o preço do lado. */}
      <section className="space-y-4">
        <h2 className="text-center text-xl font-semibold tracking-tight sm:text-2xl">
          Duas formas de entrar
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="cartao-tom faixa-tom flex flex-col" style={{ '--tom': 'var(--cor-alegre-3)' } as React.CSSProperties}>
            <CardHeader>
              <Badge tom="principal" className="w-fit">
                Grátis
              </Badge>
              <CardTitle className="pt-1">{planoGratis?.nome ?? 'Documentos — para conhecer'}</CardTitle>
              <CardDescription>
                {planoGratis?.resumo ?? 'Acesso gratuito ao editor: escreva antes de decidir.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col justify-end gap-4">
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                {(planoGratis?.recursos ?? []).map((linha) => (
                  <li key={linha} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-3.5 shrink-0" />
                    {linha}
                  </li>
                ))}
              </ul>
              <Button
                className="whitespace-normal! h-auto! py-2.5 w-full"
                tamanho="grande"
                disabled={pedindo || jaTem}
                onClick={() => void conhecer()}
              >
                {jaTem ? 'Você já tem acesso' : pedindo ? <Loader2 className="animate-spin" /> : <Sparkles />}
                {jaTem ? 'Bom trabalho' : usuario ? 'Liberar agora' : 'Criar conta e liberar'}
              </Button>
            </CardContent>
          </Card>

          <Card className="cartao-tom faixa-tom flex flex-col" style={{ '--tom': 'var(--cor-alegre-1)' } as React.CSSProperties}>
            <CardHeader>
              <Badge tom="principal" className="w-fit">
                Só este editor
              </Badge>
              <CardTitle className="pt-1">{planoPago?.nome ?? 'Documentos'}</CardTitle>
              <CardDescription>
                {planoPago?.resumo ?? 'O editor de trabalhos, artigos e livros.'}
                {planoPago ? (
                  <>
                    {' '}
                    <strong className="text-foreground">
                      {formatarPreco(planoPago.precoCentavos)} por mês.
                    </strong>
                  </>
                ) : null}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-1 flex-col justify-end gap-4">
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                {(planoPago?.recursos ?? []).map((linha) => (
                  <li key={linha} className="flex items-start gap-2">
                    <Check className="mt-0.5 size-3.5 shrink-0" />
                    {linha}
                  </li>
                ))}
              </ul>
              <Button asChild variant="contorno" tamanho="grande" className="whitespace-normal! h-auto! py-2.5 w-full">
                <Link to="/planos">
                  Ver o plano na página de planos
                  <ArrowRight />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        <p className="text-center text-xs leading-relaxed text-muted-foreground">
          Quem já monta miolos de agenda continua com os planos de sempre — esta área é do editor de
          documentos, e o acesso gratuito pode ser trocado por um plano pago quando você quiser.
        </p>
      </section>
    </div>
  );
}

/** Os tons dos enfeites dos cartões, em ciclo (os mesmos do resto do site). */
const TONS = [
  'var(--cor-alegre-1)',
  'var(--cor-alegre-3)',
  'var(--cor-alegre-4)',
  'var(--cor-alegre-2)',
  'var(--cor-alegre-5)',
  'var(--cor-alegre-1)',
];
